/**
 * Forwarding a message (D-CHAT-04, M-CHAT-04).
 *
 * Wire contract shared with MobileCh3 (`services/chat/helpers/forwardMessage.ts`):
 * the forwarded event is a copy of the original `m.room.message` content with
 * one extra field, `org.phonehq.forwarded`, that says where it came from. The
 * copy drops the original's relations (reply, edit, thread) and mentions, so
 * it neither pings anyone again nor points into a room the reader may not see.
 * Both clients are inside E2EE, so this field is only readable by members.
 */

export const FORWARDED_KEY = 'org.phonehq.forwarded';

export interface ForwardedFrom {
    /** Room the original was posted in. */
    readonly room_id: string;
    readonly event_id: string;
    /** Matrix id of the original author. */
    readonly sender: string;
    /** Their display name when it was forwarded. */
    readonly sender_name?: string;
    /** Name of the source room when it was a group; absent for a DM. */
    readonly room_name?: string;
}

const FORWARDABLE_MSGTYPES = new Set(['m.text', 'm.notice', 'm.emote', 'm.image', 'm.file', 'm.audio', 'm.video']);

/** Text and media can be forwarded; calls, polls and our own control events cannot. */
export function isForwardableContent(content: Record<string, unknown> | null | undefined): boolean {
    return !!content && typeof content.msgtype === 'string' && FORWARDABLE_MSGTYPES.has(content.msgtype);
}

/**
 * The content to send into the target room. Keeps body, format, media and
 * encrypted-file keys; drops relations, mentions and any earlier forward
 * marker (a forward of a forward points at the latest hop).
 */
export function buildForwardContent(
    original: Record<string, unknown>,
    from: ForwardedFrom,
): Record<string, unknown> {
    const rest: Record<string, unknown> = {...original};
    for (const key of ['m.relates_to', 'm.mentions', 'm.new_content', FORWARDED_KEY]) delete rest[key];
    let body = rest.body;
    let formatted = rest.formatted_body;
    // A reply's body starts with the quoted fallback ("> <@a> ...\n\n");
    // without the relation it would read as part of the message.
    if (typeof body === 'string' && body.startsWith('> ')) {
        const split = body.indexOf('\n\n');
        if (split !== -1) body = body.slice(split + 2);
    }
    if (typeof formatted === 'string') {
        formatted = formatted.replace(/^<mx-reply>[\s\S]*?<\/mx-reply>/, '');
    }
    return {
        ...rest,
        ...(body !== undefined ? {body} : {}),
        ...(formatted !== undefined ? {formatted_body: formatted} : {}),
        [FORWARDED_KEY]: {...from},
    };
}

export function readForwardedFrom(content: Record<string, unknown> | null | undefined): ForwardedFrom | null {
    const value = content?.[FORWARDED_KEY];
    if (!value || typeof value !== 'object') return null;
    const v = value as Partial<ForwardedFrom>;
    if (typeof v.room_id !== 'string' || typeof v.event_id !== 'string' || typeof v.sender !== 'string') return null;
    return v as ForwardedFrom;
}

/**
 * "Forwarded from Tech · Alex Doe" for a group, "Forwarded from Alex Doe"
 * for a DM.
 */
export function forwardedLabel(from: ForwardedFrom): string {
    const who = from.sender_name || from.sender.replace(/^@/, '').split(':')[0];
    return from.room_name ? `Forwarded from ${from.room_name} · ${who}` : `Forwarded from ${who}`;
}

/**
 * An encrypted message must not be re-sent into a room without encryption
 * (M-CHAT-04/S03: "Not encrypted · can't receive this message").
 */
export function canForwardInto(sourceEncrypted: boolean, targetEncrypted: boolean): boolean {
    return !sourceEncrypted || targetEncrypted;
}

/** "Forwarded to 1 chat" / "Forwarded to 3 chats". */
export function forwardedToast(count: number): string {
    return `Forwarded to ${count} ${count === 1 ? 'chat' : 'chats'}`;
}
